/**
 * PT-5644: whether the visitor has dismissed the install nudge, remembered across surfaces and
 * relaunches.
 *
 * The nudge's dismissed flag used to live in component-local `useState`. The nudge renders only on
 * the login surface (`app.tsx`), so a fresh `useState(false)` made it pop back up on every relaunch
 * and on any remount of that surface — a login↔discovering/unavailable view flip, or returning to
 * login after logout — since nothing was stored. Persisting an explicit dismissal here makes it
 * stick — once a visitor closes the banner they are not nagged again until they install (installing
 * removes the nudge on its own via the standalone/installed checks).
 *
 * Home is localStorage, a sibling of `vn-pwa:api-domain` / `vn-pwa:api-index`: this is a UI
 * preference read synchronously as the nudge's initial state, with no auth concern. It fails open —
 * a missing, blocked, corrupt, or non-marker value reports "not dismissed" so an uninstalled
 * visitor is still nudged; only the exact persisted marker suppresses it.
 */
export const INSTALL_NUDGE_DISMISSED_STORAGE_KEY = 'vn-pwa:install-nudge-dismissed'

/** The single value that means "dismissed"; any other stored value fails open to showing the nudge. */
const DISMISSED_MARKER = '1'

/**
 * Resolve `localStorage`, never throwing. In sandboxed in-app/WebView browsers even *reading*
 * `window.localStorage` can raise a SecurityError, and because default parameters are evaluated at
 * the call site before the callee's own try/catch runs, that read must be guarded here rather than
 * left as a bare default value.
 */
const resolveStorage = (): Pick<Storage, 'getItem' | 'setItem'> | undefined => {
  try {
    return localStorage
  } catch {
    return undefined
  }
}

/**
 * Remember that the visitor dismissed the install nudge so it stays hidden across the login→
 * authenticated remount and future relaunches. Never throws — not even via its default parameter —
 * because a blocked write is fully recoverable (the nudge simply reappears) and must not break the
 * dismiss click.
 */
export const persistNudgeDismissed = (storage: Pick<Storage, 'setItem'> | undefined = resolveStorage()): void => {
  try {
    storage?.setItem(INSTALL_NUDGE_DISMISSED_STORAGE_KEY, DISMISSED_MARKER)
  } catch {
    // Storage blocked; the nudge reappearing on the next surface/launch is the fallback.
  }
}

/**
 * Read whether the visitor previously dismissed the install nudge. Fails open: only the exact
 * persisted marker suppresses the nudge; a missing, blocked, corrupt, or unexpected value reports
 * `false` so an uninstalled visitor who never dismissed is still prompted to install.
 */
export const loadNudgeDismissed = (storage: Pick<Storage, 'getItem'> | undefined = resolveStorage()): boolean => {
  try {
    return storage?.getItem(INSTALL_NUDGE_DISMISSED_STORAGE_KEY) === DISMISSED_MARKER
  } catch {
    return false
  }
}
